<template>
  <div>
    <el-dialog
      :visible.sync="dialogVisible"
      width="1000px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      @close="handleClose"
      custom-class="all-feedback-dialog"
    >
      <div slot="title" class="dialog-header">
        <div class="dialog-title">我的建议记录</div>
        <div class="dialog-subtitle">追踪您的历史反馈及回复状态。</div>
      </div>
      <!-- 筛选和搜索区域 -->
      <div class="filter-section">
        <div class="status-tabs">
          <el-button 
            type="primary" 
            size="small"
            :plain="activeStatus !== '全部'" 
            @click="setActiveStatus('全部')"
          >全部</el-button>
          <el-button 
            type="primary" 
            size="small"
            :plain="activeStatus !== '处理中'" 
            @click="setActiveStatus('处理中')"
          >处理中</el-button>
          <el-button 
            type="primary" 
            size="small"
            :plain="activeStatus !== '已回复'" 
            @click="setActiveStatus('已回复')"
          >已回复</el-button>
        </div>
        <div class="search-section">
          <div style="display: flex; gap: 10px; align-items: center">
            <div style="display: flex; align-items: center; gap: 8px">
              <label style="font-size: 14px; color: #606266; white-space: nowrap">标题搜索</label>
              <el-input
                v-model="titleKeyword"
                placeholder="请输入标题"
                maxlength="100"
                style="width: 200px;"
                size="small" clearable
                @keyup.enter.native="handleSearch"
                @clear="handleSearch"
              ></el-input>
            </div>
            <div style="display: flex; align-items: center; gap: 8px">
              <label style="font-size: 14px; color: #606266; white-space: nowrap">内容搜索</label>
              <el-input
                v-model="contentKeyword"
                placeholder="请输入内容描述"
                maxlength="200"
                style="width: 200px;"
                size="small" clearable
                @keyup.enter.native="handleSearch"
                @clear="handleSearch"
              ></el-input>
            </div>
            <div style="display: flex; align-items: center; gap: 0px">
              <el-button type="primary" size="small" @click="handleSearch">搜索</el-button>
              <el-button size="small" @click="resetSearch">重置</el-button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 反馈列表 -->
      <div class="feedback-list">
        <el-table
          ref="feedbackTable"
          v-loading="loading"
          :data="feedbackList"
          style="width: 100%"
          max-height="500"
          border
          :empty-text="loading ? '加载中...' : '暂无数据'"
          @sort-change="handleSortChange"
        >
          <el-table-column prop="title" label="标题" min-width="200" show-overflow-tooltip header-align="left" sortable="custom" :sort-orders="['descending', 'ascending', null]"></el-table-column>
          <el-table-column prop="content" label="描述" min-width="300" show-overflow-tooltip header-align="left" sortable="custom" :sort-orders="['descending', 'ascending', null]">
            <template slot-scope="scope">
              <span>{{ scope.row.content.substring(0, 50) }}{{ scope.row.content.length > 50 ? '...' : '' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="create_time" label="提交时间" min-width="150" align="left" header-align="left" sortable="custom" :sort-orders="['descending', 'ascending', null]">
            <template slot-scope="scope">
              <span>{{ scope.row.create_time }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="reply_status" label="状态" min-width="100" align="center" header-align="center" sortable="custom" :sort-orders="['descending', 'ascending', null]">
            <template slot-scope="scope">
              <el-tag 
                :type="getStatusType(scope.row.reply_status)" 
                size="small"
                class="status-tag"
              >{{ getStatusText(scope.row.reply_status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" min-width="120" align="center" header-align="center">
            <template slot-scope="scope">
              <el-button 
                type="text" 
                @click="viewDetail(scope.row)"
              >
                {{ scope.row.reply_status === 'replied' ? '查看回复' : '查看详情' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页 -->
        <div class="pagination-section">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.currentPage"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
          ></el-pagination>
          <el-button style="margin:0 0 0 10px" @click="handleClose">关闭</el-button>
        </div>
      </div>
    </el-dialog>
    <!-- 查看反馈详情弹窗 -->
    <FeedbackDetail :visible="feedbackDetailVisible" :feedback-data="currentFeedback" @close="feedbackDetailVisible = false" />
    <!-- 查看回复弹窗 -->
    <FeedbackView :visible="feedbackViewVisible" :feedback-data="currentFeedback" :loading="feedbackLoading" title="用户之声 - 查看回复" @close="feedbackViewVisible = false" />
  </div>
</template>

<script>
// 导入getFeedbackList接口
import { getFeedbackList, getFeedbackDetail } from '@/api/ai/agent';
import FeedbackDetail from './FeedbackDetail.vue';
import FeedbackView from './FeedbackView.vue';

export default {
  name: "AllFeedbackDialog",
  components: {
    FeedbackDetail,
    FeedbackView
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    recentFeedbacks: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    userId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      // 本地弹窗状态
      dialogVisible: this.visible,
      // 活跃状态
      activeStatus: '全部',
      // 搜索关键词
      titleKeyword: '',
      contentKeyword: '',
      // 加载状态
      loading: false,
      // 分页参数
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      // 反馈列表数据
      feedbackList: [],
      // 反馈详情相关
      feedbackDetailVisible: false,
      feedbackViewVisible: false,
      currentFeedback: {},
      feedbackLoading: false,
      // 排序相关
      sortProp: '',
      sortOrder: ''
    }
  },
  watch: {
    // 监听父组件传递的recentFeedbacks变化
    recentFeedbacks: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.feedbackList = newVal.slice(0, 10);
        } else {
          this.feedbackList = [];
        }
      },
      immediate: true
    },
    // 监听父组件传递的total变化
    total: {
      handler(newVal) {
        this.pagination.total = newVal;
      },
      immediate: true
    },
    // 监听父组件传递的visible变化
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal;
        if (newVal) {
          this.initData();
        }
      },
      immediate: true
    },
    // 监听本地dialogVisible变化，通知父组件
    dialogVisible(newVal) {
      this.$emit('update:visible', newVal);
      if (!newVal) {
        this.$emit('close');
      }
    }
  },
  methods: {
    // 获取状态对应的标签类型
    getStatusType(reply_status) {
      switch (reply_status) {
        case 'processing':
          return 'warning';
        case 'replied':
          return 'success';
        case 'none':
          return 'info';
        default:
          return 'info';
      }
    },
    // 获取状态对应的中文文本
    getStatusText(reply_status) {
      switch (reply_status) {
        case 'none':
          return '已收到';
        case 'processing':
          return '处理中';
        case 'replied':
          return '已完成';
        default:
          return '已收到';
      }
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.getFullYear() + '年' + 
             (date.getMonth() + 1).toString().padStart(2, '0') + '月' + 
             date.getDate().toString().padStart(2, '0') + '日';
    },
    
    // 初始化数据
    initData() {
      this.activeStatus = '全部';
      this.titleKeyword = '';
      this.contentKeyword = '';
      this.pagination.currentPage = 1;
      this.pagination.pageSize = 10;
      if (this.recentFeedbacks && this.recentFeedbacks.length > 0) {
        this.feedbackList = this.recentFeedbacks.slice(0, 10);
      } else {
        this.feedbackList = [];
      }
      this.pagination.total = this.total || 0;
    },
    
    // 设置活跃状态
    setActiveStatus(status) {
      if (this.activeStatus === status) {
        return;
      }
      this.activeStatus = status;
      this.pagination.currentPage = 1;
      this.loadFeedbackList();
    },
    
    // 处理搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.loadFeedbackList();
    },
    
    // 重置搜索
    resetSearch() {
      this.titleKeyword = '';
      this.contentKeyword = '';
      this.pagination.currentPage = 1;
      this.loadFeedbackList();
    },
    
    // 加载反馈列表
    async loadFeedbackList() {
      this.loading = true;
      try {
        const params = {
          user_id: this.userId,
          order_by: this.getOrderBy(),
          page: this.pagination.currentPage,
          page_size: this.pagination.pageSize
        };
        
        // 添加状态筛选
        if (this.activeStatus !== '全部') {
          const statusMap = {
            '处理中': 'processing',
            '已回复': 'replied'
          };
          params.reply_status = statusMap[this.activeStatus];
        }
        
        // 添加搜索关键词
        const trimmedTitleKeyword = this.titleKeyword ? this.titleKeyword.trim() : '';
        const trimmedContentKeyword = this.contentKeyword ? this.contentKeyword.trim() : '';
        if (trimmedTitleKeyword) {
          params.title_like = trimmedTitleKeyword;
        }
        if (trimmedContentKeyword) {
          params.content_like = trimmedContentKeyword;
        }
        
        const response = await getFeedbackList(params);
        if (response.code === 200 && response.data) {
          // 更新分页信息
          this.pagination.total = response.data.total || 0;
          // 更新反馈列表数据
          this.feedbackList = response.data.data || [];
        }
      } catch (error) {
        console.error('加载反馈列表失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // 查看详情
    async viewDetail(row) {
      // 根据回复状态判断显示哪个组件
      if (row.reply_status === 'replied') {
        // 如果已回复，先打开弹窗，然后调用接口获取详细数据
        this.currentFeedback = {};
        this.feedbackViewVisible = true;
        try {
          this.feedbackLoading = true;
          const response = await getFeedbackDetail(row.id, {
            feedback_id: row.id,
            user_id: this.userId
          });
          if (response && response.code === 200) {
            this.currentFeedback = response.data;
          } else {
            this.$message.error(response.message || '获取反馈详情失败');
            this.feedbackViewVisible = false;
          }
        } catch (error) {
          console.error('获取反馈详情失败:', error);
          this.$message.error('获取反馈详情失败，请稍后重试');
          this.feedbackViewVisible = false;
        } finally {
          this.feedbackLoading = false;
        }
      } else {
        // 否则直接显示详情
        this.currentFeedback = row;
        this.feedbackDetailVisible = true;
      }
    },
    
    // 处理页码变化
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.loadFeedbackList();
    },
    
    // 处理当前页变化
    handleCurrentChange(current) {
      this.pagination.currentPage = current;
      this.loadFeedbackList();
    },
    
    // 处理弹窗关闭
    handleClose() {
      this.dialogVisible = false;
    },
    // 处理表格排序
    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      // 重新加载数据
      this.loadFeedbackList();
    },
    // 生成排序参数
    getOrderBy() {
      if (!this.sortProp) {
        return '-id'; // 默认按ID降序
      }
      if (this.sortOrder === 'ascending') {
        return this.sortProp; // 升序
      } else if (this.sortOrder === 'descending') {
        return '-' + this.sortProp; // 降序
      } else {
        return '-id'; // 默认按ID降序
      }
    }
  }
}
</script>

<style scoped lang="less">
/* 弹窗头部样式 */
.dialog-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  .dialog-title {
    font-size: 18px;
    color: #666;
  }
  .dialog-subtitle {
    font-size: 12px;
    color: #409EFF;
    margin-top: 4px;
  }
}

/* 筛选区域样式 */
.filter-section {
  margin:5px 0 10px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .status-tabs {
    display: flex;
    gap: 0px;
    .el-button {
      border-radius: 20px;
      &.el-button--primary.is-plain {
        background-color: #f5f7fa;
        border-color: #dcdfe6;
        color: #909399;
        &:hover {
          background-color: #ecf5ff;
          border-color: #c6e2ff;
          color: #409eff;
        }
      }
    }
  }
  .search-section {
    display: flex;
    align-items: center;
  }
}

/* 反馈列表样式 */
.feedback-list {
  width:100%;
}

/* 分页样式 */
.pagination-section {
  padding: 10px 0 20px 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
</style>

<style lang="less">
/* 确保弹窗层级高于抽屉 */
.all-feedback-dialog {
  margin-top: 60px !important;
  .el-dialog__header {
    padding: 20px 15px 5px 20px;
  }
  .el-dialog__body {
    padding: 0px 20px;
  }
}
.search-section .el-form-item {
  margin-bottom: 0px;
}
</style>